import {StoreyTypologyType} from "@/models/types/storey-typology-type.ts";
import BuildingCreationModel from "../models/building-creation-model.ts";
import StoreyCreationModel from "../models/storey-creation-model.ts";

// ============================================================================
// REGEX & PATTERNS DE DÉTECTION (MODIFIABLES / CONTRÔLABLES EN HAUT DU FICHIER)
// ============================================================================

/** Détection des termes de toiture et locaux techniques hauts */
export const ROOF_REGEX = /(?:^|[\s_\-–])(?:toi|toiture|roof|rooftop|tt|lt|terrasse\s*technique|toiture\s*terrasse|local\s*technique|machinerie|sous[\s-]toiture)(?:[\s_\-–]|$)/iu;

/** Détection des termes d'infrastructure (sous-sol, vide sanitaire, etc.) */
export const INFRA_REGEX = /(?:^|[\s_\-–])(?:SS|S|sous\s*sol|basement|bsmt|vide\s*sanitaire|vs|radier|fosse)\s*0?\d{0,2}(?:[\s_\-–]|$)/iu;

/** Détection des documents et vues non associées à un étage particulier (coupes, façades, plans de masse, etc.) */
export const NON_STOREY_PATTERNS: Array<{
    regex: RegExp;
    formatter: (match: RegExpMatchArray) => string;
}> = [
    {
        regex: /(?:^|[\s_\-–])(?:plan\s*de\s*masse|pdm)(?:[\s_\-–]|$)/iu,
        formatter: () => "Plan de masse",
    },
    {
        regex: /(?:^|[\s_\-–])(?:plan\s*de\s*situation|pds)(?:[\s_\-–]|$)/iu,
        formatter: () => "Plan de situation",
    },
    {
        regex: /(?:^|[\s_\-–])fa[çc]ades?(?:\s+(?:nord|sud|est|ouest|principale|arri[eè]re|[a-z0-9]+))?(?:[\s_\-–]|$)/iu,
        formatter: match => {
            const raw = match[0].trim();
            return raw.charAt(0).toUpperCase() + raw.slice(1);
        },
    },
    {
        regex: /(?:^|[\s_\-–])coupes?(?:\s+[a-z0-9\-–]+)?(?:[\s_\-–]|$)/iu,
        formatter: match => {
            const raw = match[0].trim();
            return raw.charAt(0).toUpperCase() + raw.slice(1);
        },
    },
    {
        regex: /(?:^|[\s_\-–])(?:rep[eé]rage|vue\s*3d|d[eé]tails?)(?:[\s_\-–]|$)/iu,
        formatter: match => {
            const raw = match[0].trim();
            return raw.charAt(0).toUpperCase() + raw.slice(1);
        },
    },
];

/** Motifs explicites de détection des bâtiments dans le texte */
export const BUILDING_EXPLICIT_PATTERNS: Array<{
    regex: RegExp;
    confidence: number;
    formatter: (match: RegExpMatchArray) => string;
}> = [
    {
        regex: /(?:^|[\s_\-–])(?:b[âa]timent|batiment|building|immeuble)\s*([a-z0-9]+)(?:[\s_\-–]|$)/iu,
        confidence: 0.98,
        formatter: match => `Bâtiment ${match[1].toUpperCase()}`,
    },
    {
        regex: /(?:^|[\s_\-–])(bloc|plot|hall|cage|villa|pavillon|[iî]lot|tour|tranche|phase)\s*([a-z0-9]+)(?:[\s_\-–]|$)/iu,
        confidence: 0.98,
        formatter: match => {
            const raw = match[1].toLowerCase();
            const prefix = raw.startsWith("i") || raw.startsWith("î")
                ? "Îlot"
                : raw.charAt(0).toUpperCase() + raw.slice(1);
            return `${prefix} ${match[2].toUpperCase()}`;
        },
    },
    {
        regex: /\bBAT\s*([A-Z0-9]+)\b/i,
        confidence: 0.95,
        formatter: match => `BAT ${match[1].toUpperCase()}`,
    },
    {
        regex: /\bBAT([A-Z0-9]+)\b/i,
        confidence: 0.95,
        formatter: match => `BAT ${match[1].toUpperCase()}`,
    },
    {
        regex: /\b(?:BL|HL|TR|PH|VIL)\s*[-_]?\s*([A-Z0-9]+)\b/i,
        confidence: 0.9,
        formatter: match => match[0].toUpperCase().replace(/\s+/g, " "),
    },
];

/** Détection du bâtiment dans le code projet (ex: BAT01, BATA, BLOC-A, PLOT1, etc.) */
export const BUILDING_PROJECT_CODE_REGEX = /(?:^|-|\s)((?:BAT|BLOC|PLOT|HALL|CAGE|VILLA|PAVILLON|ILOT|ÎLOT|TOUR|TRANCHE|PHASE)[A-Z0-9]+)(?:-|\s|$)/i;

/** Détection du bâtiment dans un token individuel (ex: BAT1, BATA, BLOC01, PLOT1) */
export const BUILDING_TOKEN_REGEX = /^(?:BAT|BLOC|PLOT|HALL|CAGE|VILLA|PAVILLON|ILOT|TOUR|TR|PH)[A-Z0-9]+$/i;

/** Motifs explicites de détection des étages dans le texte */
export const FLOOR_EXPLICIT_PATTERNS: Array<{
    regex: RegExp;
    confidence: number;
    formatter: (match: RegExpMatchArray) => string;
    extract?: (match: RegExpMatchArray) => { levelNumber?: number; isRoof?: boolean; isAttic?: boolean };
}> = [
    {
        // Rez inférieur / RDC inférieur / RDC bas / Rez bas = Étage 0
        regex: /(?:^|[\s_\-–])(?:rez\s*inf[eé]rieur|rdc\s*inf[eé]rieur|rdc\s*inf|rez\s*inf|rdc\s*bas|rez\s*bas)(?:[\s_\-–]|$)/iu,
        confidence: 0.98,
        formatter: () => "Rez inférieur",
        extract: () => ({ levelNumber: 0 }),
    },
    {
        // Rez-de-jardin / RDJ = Étage 0
        regex: /(?:^|[\s_\-–])(?:rez\s*de\s*jardin|rdj)(?:[\s_\-–]|$)/iu,
        confidence: 0.98,
        formatter: () => "Rez-de-jardin",
        extract: () => ({ levelNumber: 0 }),
    },
    {
        // Rez supérieur / RDC supérieur / RDC Haut / Rez Haut (étage intermédiaire)
        regex: /(?:^|[\s_\-–])(?:rez\s*sup[eé]rieur|rdc\s*sup[eé]rieur|rdc\s*sup|rez\s*sup|rdc\s*haut|rez\s*haut)(?:[\s_\-–]|$)/iu,
        confidence: 0.98,
        formatter: match => {
            const val = match[0].toLowerCase();
            if (val.includes("haut")) {
                return "RDC Haut";
            }
            return "Rez supérieur";
        },
        extract: () => ({ }),
    },
    {
        // Entresol / Mezzanine (étage intermédiaire au-dessus du RDC)
        regex: /(?:^|[\s_\-–])(?:entresol|mezzanine|mezz)(?:[\s_\-–]|$)/iu,
        confidence: 0.98,
        formatter: match => {
            const val = match[0].toLowerCase();
            if (val.includes("entresol")) {
                return "Entresol";
            }
            return "Mezzanine";
        },
        extract: () => ({ }),
    },
    {
        // Combles / Surcombles / Sous-toiture (avant-dernier étage / niveau comble)
        regex: /(?:^|[\s_\-–])(?:combles|surcombles|sous[\s-]toiture)(?:[\s_\-–]|$)/iu,
        confidence: 0.98,
        formatter: match => {
            const val = match[0].toLowerCase();
            if (val.includes("surcombles")) {
                return "Surcombles";
            }
            return "Combles";
        },
        extract: () => ({ isAttic: true }),
    },
    {
        // Attique (superstructure avant la toiture)
        regex: /(?:^|[\s_\-–])attique(?:[\s_\-–]|$)/iu,
        confidence: 0.98,
        formatter: () => "Attique",
        extract: () => ({ isAttic: true }),
    },
    {
        // Vide sanitaire / Radier / Fosse (infrastructure niveau -1)
        regex: /(?:^|[\s_\-–])(?:vide\s*sanitaire|vs|radier|fosse(?:\s*d['’]ascenseur)?)(?:[\s_\-–]|$)/iu,
        confidence: 0.98,
        formatter: match => {
            const val = match[0].toLowerCase();
            if (val.includes("vs") || val.includes("vide")) {
                return "Vide sanitaire";
            }
            if (val.includes("radier")) {
                return "Radier";
            }
            return "Fosse";
        },
        extract: () => ({ levelNumber: -1 }),
    },
    {
        // Terrasse technique / Local technique / TT / LT / Rooftop / Toiture / Roof / TOI
        regex: /(?:^|[\s_\-–])(?:toiture\s*terrasse|terrasse\s*technique|local\s*technique|machinerie|rooftop|toiture|toi|roof|tt|lt)(?:[\s_\-–]|$)/iu,
        confidence: 0.98,
        formatter: match => {
            const value = match[0].trim().toLowerCase();
            if (value === "toi") {
                return "TOI";
            }
            if (value === "roof" || value === "rooftop") {
                return "Roof";
            }
            if (value === "tt" || value.includes("terrasse technique")) {
                return "Terrasse technique";
            }
            if (value === "lt" || value.includes("local technique")) {
                return "Local technique";
            }
            return "Toiture";
        },
        extract: () => ({ isRoof: true }),
    },
    {
        // RDC / Rez-de-chaussée = Étage 0
        regex: /(?:^|[\s_\-–])(?:rdc|rez\s*de\s*chauss[eé]e)(?:[\s_\-–]|$)/iu,
        confidence: 0.98,
        formatter: () => "RDC",
        extract: () => ({ levelNumber: 0 }),
    },
    {
        // BIM & International: Ground Floor / GF = Étage 0
        regex: /(?:^|[\s_\-–])(?:ground\s*floor|gf)(?:[\s_\-–]|$)/iu,
        confidence: 0.95,
        formatter: () => "GF",
        extract: () => ({ levelNumber: 0 }),
    },
    {
        // BIM & International: Upper Ground / UG (étage intermédiaire)
        regex: /(?:^|[\s_\-–])(?:upper\s*ground|ug)(?:[\s_\-–]|$)/iu,
        confidence: 0.95,
        formatter: () => "UG",
        extract: () => ({ }),
    },
    {
        // BIM & International: Lower Ground / LG = Étage 0
        regex: /(?:^|[\s_\-–])(?:lower\s*ground|lg)(?:[\s_\-–]|$)/iu,
        confidence: 0.95,
        formatter: () => "LG",
        extract: () => ({ levelNumber: 0 }),
    },
    {
        // BIM & International: MZ (Mezzanine)
        regex: /(?:^|[\s_\-–])mz(?:[\s_\-–]|$)/iu,
        confidence: 0.95,
        formatter: () => "MZ",
        extract: () => ({ }),
    },
    {
        // BIM & International: Level / Floor 00 (L00, FL00)
        regex: /(?:^|[\s_\-–])(?:FL|L)\s*0{1,2}(?:[\s_\-–]|$)/iu,
        confidence: 0.95,
        formatter: () => "L00",
        extract: () => ({ levelNumber: 0 }),
    },
    {
        // BIM & International: Level / Floor X (ex: L01, FL02, L1, FL12)
        regex: /(?:^|[\s_\-–])(?:FL|L)\s*0?(\d{1,2})(?:[\s_\-–]|$)/iu,
        confidence: 0.95,
        formatter: match => `L${match[1].padStart(2, "0")}`,
        extract: match => ({ levelNumber: Number(match[1]) }),
    },
    {
        // BIM & International: Basement (B1, B2, BSMT01, BSMT1)
        regex: /(?:^|[\s_\-–])(?:BSMT|B)\s*0?(\d{1,2})(?:[\s_\-–]|$)/iu,
        confidence: 0.92,
        formatter: match => `B${Number(match[1])}`,
        extract: match => ({ levelNumber: -Number(match[1]) }),
    },
    {
        // R+X
        regex: /(?:^|[\s_\-–])R\s*\+\s*(\d{1,2})(?:[\s_\-–]|$)/iu,
        confidence: 0.95,
        formatter: match => `R+${Number(match[1])}`,
        extract: match => ({ levelNumber: Number(match[1]) }),
    },
    {
        // R-X
        regex: /(?:^|[\s_\-–])R\s*-\s*(\d{1,2})(?:[\s_\-–]|$)/iu,
        confidence: 0.95,
        formatter: match => `R-${Number(match[1])}`,
        extract: match => ({ levelNumber: -Number(match[1]) }),
    },
    {
        // RX (ex: R1, R2, R0)
        regex: /(?:^|[\s_\-–])R\s*([+-]?\d{1,2})(?:[\s_\-–]|$)/iu,
        confidence: 0.95,
        formatter: match => `R${match[1]}`,
        extract: match => ({ levelNumber: Number(match[1]) }),
    },
    {
        // N 01, N02
        regex: /(?:^|[\s_\-–])N\s*0?(\d{1,2})(?:[\s_\-–]|$)/iu,
        confidence: 0.95,
        formatter: match => `N ${match[1].padStart(2, "0")}`,
        extract: match => ({ levelNumber: Number(match[1]) }),
    },
    {
        // Niveau 1, Étage 2, Floor 3
        regex: /(?:^|[\s_\-–])(?:niveau|niv|[eé]tage|floor|level)\s*0?(\d{1,2})(?:[\s_\-–]|$)/iu,
        confidence: 0.92,
        formatter: match => `Niveau ${Number(match[1])}`,
        extract: match => ({ levelNumber: Number(match[1]) }),
    },
    {
        // S1, SS1, S02
        regex: /(?:^|[\s_\-–])(?:SS|S)\s*0?(\d{1,2})(?:[\s_\-–]|$)/iu,
        confidence: 0.9,
        formatter: match => `S${Number(match[1])}`,
        extract: match => ({ levelNumber: -Number(match[1]) }),
    },
    {
        // Sous-sol 1, Basement 1
        regex: /(?:^|[\s_\-–])(?:sous\s*sol|basement)\s*0?(\d{1,2})(?:[\s_\-–]|$)/iu,
        confidence: 0.9,
        formatter: match => `Sous-sol ${Number(match[1])}`,
        extract: match => ({ levelNumber: -Number(match[1]) }),
    },
];

// ============================================================================
// TYPES & INTERFACES INTERNES
// ============================================================================

interface EntityCandidate {
    name: string;
    normalizedName: string;
    confidence: number;
    levelNumber?: number;
    isRoof?: boolean;
    isAttic?: boolean;
}

interface LevelKindCandidate {
    kind: StoreyTypologyType;
    confidence: number;
}

interface IntermediateStorey {
    name: string;
    levelKind: StoreyTypologyType;
    levelNumber?: number;
    isRoof?: boolean;
    isAttic?: boolean;
    file: File;
    originalIndex: number;
}

// ============================================================================
// FONCTIONS UTILITAIRES
// ============================================================================

const normalizeComparable = (value: string): string => value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const isRoof = (value: string): boolean => ROOF_REGEX.test(value);

const isInfra = (value: string): boolean => INFRA_REGEX.test(value);

const normalizePlanFileName = (fileName: string): string => fileName
    .replace(/\.pdf$/i, "")
    .replace(/_+/g, " ")
    .replace(/\s*-\s*/g, " - ")
    .replace(/\s+/g, " ")
    .trim();

const detectLevelKind = (
    normalizedName: string,
    floorName: string | null,
): LevelKindCandidate => {
    const comparableName = normalizeComparable(normalizedName);
    const comparableFloorName = floorName ? normalizeComparable(floorName) : "";

    if (isRoof(comparableName) || isRoof(comparableFloorName)) {
        return {
            kind: "TOIT",
            confidence: 0.98,
        };
    }

    if (isInfra(comparableName) || isInfra(comparableFloorName)) {
        return {
            kind: "INFRA",
            confidence: 0.95,
        };
    }

    return {
        kind: "SUPER",
        confidence: floorName ? 0.8 : 0,
    };
};

const tokenizePlanName = (name: string): string[] => name
    .split(/[-\s]+/g)
    .map(token => token.trim())
    .filter(Boolean);

const detectBuildingFromProjectCode = (name: string): EntityCandidate | null => {
    const match = name.match(BUILDING_PROJECT_CODE_REGEX);

    if (!match?.[1]) {
        return null;
    }

    const buildingName = match[1].toUpperCase();

    return {
        name: buildingName,
        normalizedName: normalizeComparable(buildingName),
        confidence: 0.9,
    };
};

const detectExplicitFloor = (text: string): EntityCandidate | null => {
    const matchedPattern = FLOOR_EXPLICIT_PATTERNS.find(pattern => pattern.regex.test(text));

    if (!matchedPattern) {
        return null;
    }

    const match = text.match(matchedPattern.regex);

    if (!match) {
        return null;
    }

    const name = matchedPattern.formatter(match);
    const extra = matchedPattern.extract ? matchedPattern.extract(match) : {};

    return {
        name,
        normalizedName: normalizeComparable(name),
        confidence: matchedPattern.confidence,
        ...extra,
    };
};

const detectFloorFromEnd = (tokens: string[]): EntityCandidate | null => {
    const ignoredLastTokens = new Set(["l", "m", "a", "b", "c", "d"]);
    const lastToken = tokens.at(-1);

    if (!lastToken) {
        return null;
    }

    const normalizedLastToken = normalizeComparable(lastToken);

    if (ignoredLastTokens.has(normalizedLastToken)) {
        return null;
    }

    const rPlusMatch = lastToken.match(/^R\+?(\d{1,2})$/i);
    if (rPlusMatch) {
        const name = lastToken.toUpperCase();
        return {
            name,
            normalizedName: normalizeComparable(name),
            confidence: 0.9,
            levelNumber: Number(rPlusMatch[1]),
        };
    }

    const rMinusMatch = lastToken.match(/^R-(\d{1,2})$/i);
    if (rMinusMatch) {
        const name = lastToken.toUpperCase();
        return {
            name,
            normalizedName: normalizeComparable(name),
            confidence: 0.9,
            levelNumber: -Number(rMinusMatch[1]),
        };
    }

    const bimLevelMatch = lastToken.match(/^(?:FL|L)(\d{1,2})$/i);
    if (bimLevelMatch) {
        const num = Number(bimLevelMatch[1]);
        const name = `L${bimLevelMatch[1].padStart(2, "0")}`;
        return {
            name,
            normalizedName: normalizeComparable(name),
            confidence: 0.9,
            levelNumber: num,
        };
    }

    const bimBasementMatch = lastToken.match(/^(?:BSMT|B)(\d{1,2})$/i);
    if (bimBasementMatch) {
        const num = Number(bimBasementMatch[1]);
        const name = `B${num}`;
        return {
            name,
            normalizedName: normalizeComparable(name),
            confidence: 0.9,
            levelNumber: -num,
        };
    }

    const nMatch = lastToken.match(/^N(\d{1,2})$/i);
    if (nMatch) {
        const floorNumber = nMatch[1].padStart(2, "0");
        const name = `N ${floorNumber}`;
        return {
            name,
            normalizedName: normalizeComparable(name),
            confidence: 0.85,
            levelNumber: Number(nMatch[1]),
        };
    }

    const sMatch = lastToken.match(/^(?:SS|S)(\d{1,2})$/i);
    if (sMatch) {
        const name = lastToken.toUpperCase();
        return {
            name,
            normalizedName: normalizeComparable(name),
            confidence: 0.9,
            levelNumber: -Number(sMatch[1]),
        };
    }

    if (/^RDJ$/i.test(lastToken)) {
        return {
            name: "Rez-de-jardin",
            normalizedName: normalizeComparable("Rez-de-jardin"),
            confidence: 0.95,
            levelNumber: 0,
        };
    }

    if (/^GF$/i.test(lastToken)) {
        return {
            name: "GF",
            normalizedName: normalizeComparable("GF"),
            confidence: 0.95,
            levelNumber: 0,
        };
    }

    if (/^LG$/i.test(lastToken)) {
        return {
            name: "LG",
            normalizedName: normalizeComparable("LG"),
            confidence: 0.95,
            levelNumber: 0,
        };
    }

    if (/^(?:UG|MZ|MEZZ)$/i.test(lastToken)) {
        const name = lastToken.toUpperCase();
        return {
            name,
            normalizedName: normalizeComparable(name),
            confidence: 0.9,
        };
    }

    if (/^VS$/i.test(lastToken)) {
        return {
            name: "Vide sanitaire",
            normalizedName: normalizeComparable("Vide sanitaire"),
            confidence: 0.95,
            levelNumber: -1,
        };
    }

    if (/^(?:COMBLES|SURCOMBLES|ATTIQUE)$/i.test(lastToken)) {
        const raw = lastToken.toLowerCase();
        const name = raw.charAt(0).toUpperCase() + raw.slice(1);
        return {
            name,
            normalizedName: normalizeComparable(name),
            confidence: 0.95,
            isAttic: true,
        };
    }

    if (/^RDC$/i.test(lastToken)) {
        return {
            name: "RDC",
            normalizedName: normalizeComparable("RDC"),
            confidence: 0.95,
            levelNumber: 0,
        };
    }

    if (/^(?:TOI|TOITURE|ROOF|RF|TT|LT)$/i.test(lastToken)) {
        const upper = lastToken.toUpperCase();
        const name = (upper === "TT" || upper === "LT") ? `${upper}` : "Toiture";
        return {
            name,
            normalizedName: normalizeComparable(name),
            confidence: 0.95,
            isRoof: true,
        };
    }

    if (/^[A-Z]$/i.test(lastToken)) {
        const name = lastToken.toUpperCase();

        return {
            name,
            normalizedName: normalizeComparable(name),
            confidence: 0.6,
        };
    }

    return null;
};

const detectBuilding = (
    tokens: string[],
    normalizedName: string,
): EntityCandidate | null => {
    const text = tokens.join(" ");

    const matchedPattern = BUILDING_EXPLICIT_PATTERNS.find(pattern => pattern.regex.test(text));

    if (matchedPattern) {
        const match = text.match(matchedPattern.regex);

        if (match) {
            const name = matchedPattern.formatter(match);

            return {
                name,
                normalizedName: normalizeComparable(name),
                confidence: matchedPattern.confidence,
            };
        }
    }

    const tokenCandidate = tokens.find(token => BUILDING_TOKEN_REGEX.test(token));

    if (tokenCandidate) {
        const name = tokenCandidate.toUpperCase();

        return {
            name,
            normalizedName: normalizeComparable(name),
            confidence: 0.9,
        };
    }

    const buildingFromProjectCode = detectBuildingFromProjectCode(normalizedName);

    if (buildingFromProjectCode) {
        return buildingFromProjectCode;
    }

    return null;
};

const detectFloor = (
    tokens: string[],
    normalizedName: string,
): EntityCandidate | null => {
    const text = tokens.join(" ");
    const floorFromText = detectExplicitFloor(text);

    if (floorFromText) {
        return floorFromText;
    }

    const floorFromEnd = detectFloorFromEnd(tokens);

    if (floorFromEnd) {
        return floorFromEnd;
    }

    const floorFromOriginal = detectExplicitFloor(normalizedName);

    if (floorFromOriginal) {
        return {
            ...floorFromOriginal,
            confidence: Math.min(floorFromOriginal.confidence, 0.85),
        };
    }

    return null;
};

export const detectNonStorey = (text: string): { name: string; isNonStorey: boolean } | null => {
    const matchedPattern = NON_STOREY_PATTERNS.find(pattern => pattern.regex.test(text));

    if (!matchedPattern) {
        return null;
    }

    const match = text.match(matchedPattern.regex);

    if (!match) {
        return null;
    }

    return {
        name: matchedPattern.formatter(match),
        isNonStorey: true,
    };
};

// ============================================================================
// CALCUL DES ÉTAGES ET DE L'ORGANISATION
// ============================================================================

/**
 * Calcule et assigne les index des étages d'un bâtiment selon les règles métier :
 * - Superstructure & Toiture : le niveau avec l'altimétrie 0 (ou positive la plus basse) commence à l'index 0 puis augmente jusqu'à la toiture.
 * - Infrastructure (sous-sol) : le premier niveau infra (le plus proche du RDC) commence à 1, puis 2, jusqu'au dernier étage infra.
 */
export const assignStoreyIndexes = (storeys: StoreyCreationModel[]): StoreyCreationModel[] => {
    const basementStoreys = storeys.filter(s => s.storeyType === "INFRA" || s.isBasement);
    const superStoreys = storeys.filter(s => s.storeyType !== "INFRA" && !s.isBasement);

    // Infra : tri du niveau le plus haut (-1, proche de 0) au plus profond
    basementStoreys.sort((a, b) => {
        if (a.altimetry !== b.altimetry) {
            return b.altimetry - a.altimetry;
        }
        return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
    });

    // Super : tri du niveau le plus bas (0 ou positif le plus bas) jusqu'à la toiture
    superStoreys.sort((a, b) => {
        if (a.altimetry !== b.altimetry) {
            return a.altimetry - b.altimetry;
        }
        return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
    });

    const indexedBasement = basementStoreys.map((storey, idx) => (
        new StoreyCreationModel(storey.name, storey.altimetry, storey.storeyType, storey.file, idx + 1)
    ));

    const indexedSuper = superStoreys.map((storey, idx) => (
        new StoreyCreationModel(storey.name, storey.altimetry, storey.storeyType, storey.file, idx)
    ));

    return [...indexedBasement, ...indexedSuper];
};

const buildStoreysForBuilding = (
    items: IntermediateStorey[],
    storeyHeight: number
): StoreyCreationModel[] => {
    const h = typeof storeyHeight === "number" && !Number.isNaN(storeyHeight) && storeyHeight > 0 ? storeyHeight : 2.7;

    // Vérifier si au moins un étage a été détecté (niveau explicite, toiture, attique ou sous-sol explicite)
    const hasAnyDetected = items.some(
        item => typeof item.levelNumber === "number" || item.isRoof || item.levelKind === "TOIT" || item.isAttic
    );

    // Cas où rien n'a été détecté : les étages sont créés dans l'ordre d'insertion des fichiers, en commençant par le RDC (altimétrie 0)
    if (!hasAnyDetected) {
        const sortedItems = [...items].sort((a, b) => a.originalIndex - b.originalIndex);
        const storeys = sortedItems.map((item, index) => {
            const altimetry = Math.round(index * h * 1000) / 1000;
            let displayName = item.name;
            if (item.name === "Étage non identifié") {
                displayName = index === 0 ? "RDC" : `Étage ${index}`;
            }
            return new StoreyCreationModel(displayName, altimetry, "SUPER", item.file);
        });
        const maxAltimetry = storeys.length > 0
            ? storeys.reduce((max, s) => Math.max(max, s.altimetry), 0)
            : 0;
        const roofAltimetry = Math.round((storeys.length > 0 ? maxAltimetry + h : h) * 1000) / 1000;
        storeys.push(new StoreyCreationModel("TOIT", roofAltimetry, "TOIT"));
        return assignStoreyIndexes(storeys);
    }

    // Répartition des étages dans les catégories
    const infraItems: IntermediateStorey[] = [];
    const rdcItems: IntermediateStorey[] = [];
    const undetectedItems: IntermediateStorey[] = [];
    const positiveItems: IntermediateStorey[] = [];
    const atticItems: IntermediateStorey[] = [];
    const roofItems: IntermediateStorey[] = [];

    items.forEach(item => {
        if (item.isRoof || item.levelKind === "TOIT") {
            roofItems.push(item);
        } else if (item.isAttic) {
            atticItems.push(item);
        } else if (typeof item.levelNumber === "number" && item.levelNumber < 0) {
            infraItems.push(item);
        } else if (item.levelKind === "INFRA" && typeof item.levelNumber !== "number") {
            infraItems.push(item);
        } else if (typeof item.levelNumber === "number" && item.levelNumber === 0) {
            rdcItems.push(item);
        } else if (typeof item.levelNumber === "number" && item.levelNumber > 0) {
            positiveItems.push(item);
        } else {
            // Étages non détectés / intermédiaires (ex: RDC Haut, Mezzanine, plans non reconnus, non-storey)
            undetectedItems.push(item);
        }
    });

    const nonRoofStoreys: StoreyCreationModel[] = [];

    // 1. Sous-sols (INFRA) : altimétries négatives
    infraItems.sort((a, b) => {
        const lvlA = typeof a.levelNumber === "number" ? a.levelNumber : -1;
        const lvlB = typeof b.levelNumber === "number" ? b.levelNumber : -1;
        if (lvlA !== lvlB) {
            return lvlA - lvlB;
        }
        return a.originalIndex - b.originalIndex;
    });

    infraItems.forEach(item => {
        const lvl = typeof item.levelNumber === "number" ? item.levelNumber : -1;
        const altimetry = Math.round(lvl * h * 1000) / 1000;
        nonRoofStoreys.push(new StoreyCreationModel(item.name, altimetry, "INFRA", item.file));
    });

    // 2. RDC : altimétrie = 0 (aucun étage autre que le RDC ne doit avoir une altimétrie à 0)
    rdcItems.sort((a, b) => a.originalIndex - b.originalIndex);
    rdcItems.forEach(item => {
        nonRoofStoreys.push(new StoreyCreationModel(item.name, 0, "SUPER", item.file));
    });

    // 3. Étages non détectés / intermédiaires : placés entre le RDC et le niveau 1 véritable (altimétrie : 1*h, 2*h, ...)
    undetectedItems.sort((a, b) => a.originalIndex - b.originalIndex);
    const undetectedOffset = undetectedItems.length;

    undetectedItems.forEach((item, index) => {
        const altimetry = Math.round((index + 1) * h * 1000) / 1000;
        const displayName = undetectedItems.length > 1 && item.name === "Étage non identifié"
            ? `Étage non identifié ${index + 1}`
            : item.name;
        nonRoofStoreys.push(new StoreyCreationModel(displayName, altimetry, "SUPER", item.file));
    });

    // 4. Niveaux positifs explicites (R1, R2, ...) : décalés du nombre d'étages non détectés
    positiveItems.sort((a, b) => {
        const lvlA = a.levelNumber ?? 0;
        const lvlB = b.levelNumber ?? 0;
        if (lvlA !== lvlB) {
            return lvlA - lvlB;
        }
        return a.originalIndex - b.originalIndex;
    });

    let maxComputedPositiveLevel = undetectedOffset;
    positiveItems.forEach(item => {
        const originalLevel = item.levelNumber ?? 1;
        const shiftedLevel = originalLevel + undetectedOffset;
        if (shiftedLevel > maxComputedPositiveLevel) {
            maxComputedPositiveLevel = shiftedLevel;
        }
        const altimetry = Math.round(shiftedLevel * h * 1000) / 1000;
        nonRoofStoreys.push(new StoreyCreationModel(item.name, altimetry, "SUPER", item.file));
    });

    // 5. Attiques : positionnés au-dessus du niveau positif le plus haut
    atticItems.sort((a, b) => a.originalIndex - b.originalIndex);
    atticItems.forEach((item, index) => {
        const atticLevel = maxComputedPositiveLevel + 1 + index;
        const altimetry = Math.round(atticLevel * h * 1000) / 1000;
        nonRoofStoreys.push(new StoreyCreationModel(item.name, altimetry, "SUPER", item.file));
    });

    // Tri de tous les étages non-toiture par altimétrie croissante
    nonRoofStoreys.sort((a, b) => {
        if (a.altimetry !== b.altimetry) {
            return a.altimetry - b.altimetry;
        }
        return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
    });

    const maxNonRoofAltimetry = nonRoofStoreys.length > 0
        ? nonRoofStoreys.reduce((max, s) => Math.max(max, s.altimetry), 0)
        : 0;

    roofItems.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }));

    // 6. Toitures : systématiquement placées au sommet au-dessus du dernier étage
    // Si aucune toiture n'est détectée dans le bâtiment, on en rajoute une avec le nom "TOIT"
    const processedRoofStoreys: StoreyCreationModel[] = roofItems.length > 0
        ? roofItems.map((roof, index) => {
            const roofAltimetry = Math.round((maxNonRoofAltimetry + (index + 1) * h) * 1000) / 1000;
            return new StoreyCreationModel(roof.name, roofAltimetry, "TOIT", roof.file);
        })
        : [
            new StoreyCreationModel("TOIT", Math.round((maxNonRoofAltimetry + h) * 1000) / 1000, "TOIT")
        ];

    const storeys = [...nonRoofStoreys, ...processedRoofStoreys];
    return assignStoreyIndexes(storeys);
};

interface PreprocessedFileStorey {
    buildingCandidate: EntityCandidate | null;
    floorCandidate: EntityCandidate | null;
    levelKindCandidate: LevelKindCandidate;
    nonStoreyCandidate: { name: string; isNonStorey: boolean } | null;
    normalizedName: string;
    file: File;
    fileIndex: number;
}

const detectOrganisation = (
    files: File[],
    storeyHeight = 2.7,
    forceSingleBuilding = false
): BuildingCreationModel[] => {
    // 1. Pré-analyse de tous les fichiers pour détecter les bâtiments, étages et vues
    const preprocessedList: PreprocessedFileStorey[] = files.map((file, fileIndex) => {
        const fileName = file.name;
        const normalizedName = normalizePlanFileName(fileName);
        const tokens = tokenizePlanName(normalizedName);

        const buildingCandidate = detectBuilding(tokens, normalizedName);
        const floorCandidate = detectFloor(tokens, normalizedName);
        const nonStoreyCandidate = detectNonStorey(normalizedName);
        const levelKindCandidate = detectLevelKind(normalizedName, floorCandidate?.name ?? null);

        return {
            buildingCandidate,
            floorCandidate,
            levelKindCandidate,
            nonStoreyCandidate,
            normalizedName,
            file,
            fileIndex,
        };
    });

    // 2. Décompte des bâtiments explicites pour heuristique de propagation majoritaire / fallback
    const buildingCounts = new Map<string, { count: number; name: string }>();
    preprocessedList.forEach(item => {
        if (item.buildingCandidate) {
            const key = item.buildingCandidate.normalizedName;
            const current = buildingCounts.get(key);
            if (current) {
                current.count += 1;
            } else {
                buildingCounts.set(key, { count: 1, name: item.buildingCandidate.name });
            }
        }
    });

    // Si des bâtiments sont identifiés, trouver le bâtiment majoritaire
    let dominantBuilding: { name: string; normalizedName: string } | null = null;
    if (buildingCounts.size > 0) {
        let maxCount = 0;
        buildingCounts.forEach((info, key) => {
            if (info.count > maxCount) {
                maxCount = info.count;
                dominantBuilding = { name: info.name, normalizedName: key };
            }
        });
    }

    // Si aucun bâtiment n'est trouvé dans l'ensemble des fichiers, utiliser un fallback neutre
    const defaultFallbackBuildingName = dominantBuilding
        ? (dominantBuilding as { name: string; normalizedName: string }).name
        : "Bâtiment principal";
    const defaultFallbackNormalized = dominantBuilding
        ? (dominantBuilding as { name: string; normalizedName: string }).normalizedName
        : normalizeComparable(defaultFallbackBuildingName);

    const buildingMap = new Map<string, { buildingName: string; storeys: IntermediateStorey[] }>();

    preprocessedList.forEach(item => {
        const { buildingCandidate, floorCandidate, nonStoreyCandidate, levelKindCandidate, file, fileIndex } = item;

        const buildingName = forceSingleBuilding
            ? defaultFallbackBuildingName
            : (buildingCandidate?.name ?? defaultFallbackBuildingName);
        const normalizedBuildingName = forceSingleBuilding
            ? defaultFallbackNormalized
            : (buildingCandidate?.normalizedName ?? defaultFallbackNormalized);

        let group = buildingMap.get(normalizedBuildingName);
        if (!group) {
            group = { buildingName, storeys: [] };
            buildingMap.set(normalizedBuildingName, group);
        }

        const isRoofStorey = floorCandidate?.isRoof || levelKindCandidate.kind === "TOIT";
        const isAtticStorey = floorCandidate?.isAttic || false;
        const levelNumber = floorCandidate?.levelNumber;

        let floorName: string;
        if (floorCandidate?.name) {
            floorName = floorCandidate.name;
        } else if (nonStoreyCandidate?.name) {
            floorName = nonStoreyCandidate.name;
        } else {
            floorName = "Étage non identifié";
        }

        let { kind } = levelKindCandidate;
        if (isRoofStorey) {
            kind = "TOIT";
        } else if (levelNumber !== undefined && levelNumber < 0) {
            kind = "INFRA";
        } else if (nonStoreyCandidate) {
            kind = "SUPER";
        } else {
            kind = "SUPER";
        }

        group.storeys.push({
            name: floorName,
            levelKind: kind,
            levelNumber,
            isRoof: isRoofStorey,
            isAttic: isAtticStorey,
            file,
            originalIndex: fileIndex,
        });
    });

    const buildings: BuildingCreationModel[] = [];

    buildingMap.forEach(({ buildingName, storeys }) => {
        const calculatedStoreys = buildStoreysForBuilding(storeys, storeyHeight);
        buildings.push(new BuildingCreationModel(buildingName, calculatedStoreys));
    });

    // Tri des bâtiments par ordre alphabétique
    buildings.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }));
    
    // Définition de l'index des bâtiments (commence à 1 et s'incrémente pour chaque bâtiment) et tri des étages décroissant
    return buildings.map((building, idx) => {
        const sortedStoreys = [...building.storeys].sort((a, b) => b.altimetry - a.altimetry);
        return new BuildingCreationModel(building.name, sortedStoreys, idx + 1);
    });
};

export default detectOrganisation;